import { ChartData, ChartOptions } from 'chart.js';
import { DimensionWithMetric, UsersOverTime } from '../types/analytics';

/** Opacity of the area under a line, over the card surface. */
const LINE_FILL_ALPHA = 0.16;
/** Width of the outline of a bar or a pie slice, in the saturated hue over its tinted fill. */
const DATA_BORDER_WIDTH = 2;
/** Opacity of each bar of a ranking, from the first bar down: one hue that fades, as the old indigo ramp did. */
const RANKING_ALPHAS = [1, 0.85, 0.7, 0.58, 0.46, 0.36, 0.28];

const HEX6 = /^#[0-9a-f]{6}$/i;

const readToken = (name: string): string => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/**
 * Gives a `#rrggbb` color an opacity, as `rgba()`. The canvas of Chart.js cannot read `var()`, so the fill is computed here
 * from the resolved token (every color token is `#rrggbb`). Any other color format comes back unchanged.
 */
export const withAlpha = (color: string, alpha: number): string => {
  const hex = color.trim();
  if (!HEX6.test(hex)) return color;
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/**
 * What a series means, as a theme token of root.scss: `accent` is indigo in light and the brand yellow in dark. The color
 * is read when the chart is built, so build the chart again when the theme changes.
 */
export type ChartTone = 'accent' | 'success' | 'danger' | 'warning';

const toneColor = (tone: ChartTone): string => readToken(`--${tone}`);

const lineColors = (color: string) => ({
  borderColor: color,
  pointBorderColor: color,
  pointBackgroundColor: color,
  backgroundColor: withAlpha(color, LINE_FILL_ALPHA),
});

interface ChartWithDatasets<D> {
  datasets: D[];
}

/**
 * Paints every line of a chart from the backend with the theme color of its tone. The backend still sends fixed colors,
 * because old app bundles read them (ADR 0001 in dashboard-backend); this page replaces them so the chart follows the theme.
 */
export const themeLineChart = <D extends object, T extends ChartWithDatasets<D>>(chart: T | undefined, tone: ChartTone) => {
  if (!chart) return chart;
  const colors = lineColors(toneColor(tone));
  return { ...chart, datasets: chart.datasets.map((dataset) => ({ ...dataset, ...colors })) };
};

/** Mixes `weight` (0 to 1) of the `#rrggbb` color `a` into the `#rrggbb` color `b`, as CSS `color-mix(in srgb, a w%, b)`. */
const mixHex = (a: string, b: string, weight: number): string => {
  const rgb = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const [ca, cb] = [rgb(a), rgb(b)];
  return `#${ca
    .map((channel, i) => Math.round(channel * weight + cb[i] * (1 - weight)))
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
};

/**
 * The two colors of a data hue, as a reservation card in the calendar draws them (`dataFillProps`, src/utils/dataFill.ts):
 * the fill is the pastel `--<hue>-100` in light and `--data-fill-tint` of `--<hue>-500` over `--surface-raised` in dark;
 * the line is the saturated `--<hue>-500`, the same in both themes.
 */
const dataHueColors = (hue: string): { fill: string; line: string } => {
  const line = readToken(`--${hue}-500`);
  const tint = parseFloat(readToken('--data-fill-tint')) / 100;
  const surface = readToken('--surface-raised');
  if (tint > 0 && HEX6.test(line) && HEX6.test(surface)) return { fill: mixHex(line, surface, tint), line };
  return { fill: readToken(`--${hue}-100`), line };
};

/**
 * Paints each bar series with a data hue, for example the hue of its reservation type, the way its calendar cards are
 * painted: a tinted fill with a saturated outline. So a type has the same colors in the calendar and in the metrics.
 */
export const themeBarChartByHue = <D extends { label?: string }, T extends ChartWithDatasets<D>>(
  chart: T | undefined,
  hueOf: (label: string | undefined, index: number) => string,
) => {
  if (!chart) return chart;
  return {
    ...chart,
    datasets: chart.datasets.map((dataset, index) => {
      const { fill, line } = dataHueColors(hueOf(dataset.label, index));
      return {
        ...dataset,
        backgroundColor: fill,
        borderColor: line,
        borderWidth: DATA_BORDER_WIDTH,
        hoverBackgroundColor: line,
      };
    }),
  };
};

/**
 * Hues of the pie slices, in order. The first is indigo, not `primary`: the primary ramp follows the accent, which is the
 * brand yellow in the dark theme and would look like the third slice.
 */
const PIE_HUES = ['indigo', 'green', 'yellow', 'blue', 'orange', 'cyan', 'red'];

export const getColors = () => {
  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');
  const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
  const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

  return { textColor, textColorSecondary, surfaceBorder };
};

export const lineAggregatedData = (data: UsersOverTime[]) => {
  const { surfaceBorder, textColorSecondary } = getColors();
  const accent = toneColor('accent');

  // TODO refactorizar y hacer en un solo loop
  const chartData: ChartData = {
    labels: data.map((item) => item.date),
    datasets: [
      {
        label: '',
        data: data.map((item) => item.activeUsers),
        borderColor: accent,
        pointBorderColor: accent,
        pointBackgroundColor: accent,
        backgroundColor: 'transparent',
        pointStyle: false,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options: ChartOptions = {
    interaction: {
      intersect: false, // Tooltip appears even when not directly over a point
      mode: 'index',
    },
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    plugins: {
      legend: {
        display: false,
        // labels: {
        //   color: textColor,
        // },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
        },
        grid: {
          color: (ctx) => {
            const index = ctx.tick.value;
            return index % 3 === 0 ? surfaceBorder : 'transparent'; // Show grid line for every 3 ticks
          },
        },
      },
      y: {
        ticks: {
          color: textColorSecondary,
        },
        grid: {
          color: (ctx) => {
            const index = ctx.tick.value;
            return index % 3 === 0 ? surfaceBorder : 'transparent'; // Show grid line for every 3 ticks
          },
        },
      },
    },
  };

  return { chartData, chartOptions: options };
};

export const aggregatedData = (data: DimensionWithMetric[]) => {
  const { surfaceBorder, textColorSecondary } = getColors();
  const rows = data ?? [];

  // TODO refactorizar y hacer en un solo loop
  const accent = toneColor('accent');
  const colors = rows.map((_: any, index) => withAlpha(accent, RANKING_ALPHAS[index] ?? RANKING_ALPHAS[0]));
  const chartData = {
    labels: rows.map((item: any) => `${item.dimension} (${item.metric})`),
    datasets: [
      {
        label: '',
        data: rows.map((item: any) => item.metric),
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      },
    ],
  };

  const chartOptions: ChartOptions = {
    interaction: {
      intersect: false, // Tooltip appears even when not directly over a point
      mode: 'index',
    },
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    plugins: {
      legend: {
        display: false,
        // labels: {
        //   fontColor: textColor,
        // },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColorSecondary,
          font: {
            weight: 500,
          },
          callback: function (value) {
            const label = this.getLabelForValue(Number(value));
            return label?.length > 16 ? label.substring(0, 16) + '...' : label;
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          color: textColorSecondary,
        },
        grid: {
          color: surfaceBorder,
        },
      },
    },
  };

  return { chartData, chartOptions };
};

export const pieAggregatedData = (data: DimensionWithMetric[]) => {
  const { textColor } = getColors();

  const rows = data ?? [];

  // Each slice is drawn as a reservation card: a tinted fill with a saturated outline (dataHueColors).
  const colors = rows.map((_: any, index) => dataHueColors(PIE_HUES[index] ?? PIE_HUES[0]));
  const chartData = {
    labels: rows.map((item: any) => `${item.dimension} (${item.metric})`),
    datasets: [
      {
        label: '',
        data: rows.map((item: any) => item.metric),
        backgroundColor: colors.map(({ fill }) => fill),
        borderColor: colors.map(({ line }) => line),
        hoverBackgroundColor: colors.map(({ line }) => line),
        borderWidth: DATA_BORDER_WIDTH,
      },
    ],
  };

  const chartOptions: ChartOptions = {
    interaction: {
      intersect: false, // Tooltip appears even when not directly over a point
      mode: 'nearest',
    },
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: textColor,
        },
      },
    },
  };

  return { chartData, chartOptions };
};

export interface GetBusinessChartOptionsOptions {
  valueSuffix?: string;
  largeValueChart?: boolean;
}

export const getBusinessChartOptions = (opts?: GetBusinessChartOptionsOptions) => {
  const { valueSuffix, largeValueChart = false } = opts ?? {};
  const { textColorSecondary } = getColors();

  const baseOptions: ChartOptions = {
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    interaction: {
      intersect: false, // Tooltip appears even when not directly over a point
      mode: 'index',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {},
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: largeValueChart ? false : true,
        ticks: {
          color: textColorSecondary,
          stepSize: 1,
          maxTicksLimit: 5,
          precision: 0,
        },
        ...(largeValueChart ? {} : { suggestedMin: 0, suggestedMax: 4 }),
      },
    },
  };

  if (valueSuffix && baseOptions.scales?.y?.ticks && baseOptions.plugins?.tooltip?.callbacks) {
    baseOptions.scales.y.ticks.callback = (value: any) => `${value} ${valueSuffix}`;
    baseOptions.plugins.tooltip.callbacks.label = (context: any) => `${context.dataset.label}: ${context.raw} ${valueSuffix}`;
  }

  return baseOptions;
};
